<template>
  <v-row class="mt-3 mb-5 d-flex justify-end">
    <v-btn color="primary" prepend-icon="mdi-plus" @click="showInvite()">
      {{ $t("page.users.title.addUser") }}
    </v-btn>
  </v-row>
  <user-dialog ref="userDialog" :dialogAction="dialogAction" @updated="handleUsersUpdated()" />
  <reset-password-dialog ref="resetPassDialog" />
  <base-confirm-dialog
    v-model="deleteDialogVisible"
    @close="deleteDialogVisible = false"
    :is-loading="deleteDialogLoading"
    :title="$t('common.action.deleteUser')"
    :actionName="$t('common.action.delete')"
    @action="deleteUser()"
    width="30%"
    :showCancel="$t('common.action.cancel')"
  >
    <template #content>
      {{ $t("common.action.deleteConfirm") }}
    </template>
  </base-confirm-dialog>
  <!-- Confirm Deactivate Dialog -->
  <base-confirm-dialog
    v-model="deactivateDialogVisible"
    @close="deactivateDialogVisible = false"
    :is-loading="deactivateDialogLoading"
    :title="$t('page.users.deactivate')"
    :actionName="$t('common.action.deactivate')"
    width="30%"
    :showCancel="$t('common.action.cancel')"
  >
    <template #content>
      {{ $t("common.action.deactivateUserConfirm") }}
    </template>
  </base-confirm-dialog>
  <!-- Confirm Activate Dialog -->
  <base-confirm-dialog
    v-model="activateDialogVisible"
    @close="activateDialogVisible = false"
    :is-loading="activateDialogLoading"
    :title="$t('page.users.activate')"
    :actionName="$t('common.action.activate')"
    width="30%"
    :showCancel="$t('common.action.cancel')"
  >
    <template #content>
      {{ $t("common.action.activateUserConfirm") }}
    </template>
  </base-confirm-dialog>
  <v-card class="mt-4">
    <v-layout>
      <v-app-bar flat>
        <v-card-title class="ml-2">{{ $t("page.users.title.allUser") }}</v-card-title>
        <v-divider vertical length="2rem" class="mt-4" />
        <v-card-subtitle class="pr-0">
          {{ users.length }}
          {{ $t("page.users.title.total") }}
        </v-card-subtitle>
        <v-card-title class="pb-2 py-0">.</v-card-title>
        <v-card-subtitle class="pl-0">
          {{ contActiveUsers() }}
          {{ $t("page.users.title.active") }}
        </v-card-subtitle>
        <v-card-title class="pb-2 py-0">.</v-card-title>
        <v-card-subtitle class="pl-0">
          {{ contInvitedUsers() }}
          {{ $t("page.users.title.invited") }}
        </v-card-subtitle>
        <v-spacer />
        <v-btn icon>
          <v-icon>mdi-magnify</v-icon>
        </v-btn>
      </v-app-bar>
      <v-main>
        <v-divider />
        <v-progress-linear v-if="useUserStore().loading" indeterminate color="primary" />
        <v-table v-if="!useUserStore().loading">
          <thead>
            <tr>
              <th
                class="text-left spaced-header"
                v-for="header in headers"
                :key="header.title"
                :id="header.title"
                :class="`text-${header.align}`"
                @click="orderBy(header.path)"
              >
                {{ header.title }}
                <v-icon size="small" v-if="header.path && !!sort[header.path]">
                  {{ `mdi-arrow-${getArrow(header.path)}-thin` }}</v-icon
                >
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in paginatedUsers" :key="user.id" :value="user.id">
              <!-- Using Style for now, will be refactored to use v-data-table -->
              <td class="text-center" style="width: 100px">
                <v-img src="@/shared/assets/user-avatar.png" />
              </td>
              <td>
                <div>
                  <v-list-item-title>{{ user.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ user.email }}</v-list-item-subtitle>
                </div>
              </td>
              <td class="text-center">
                <status-chip :status="user.role" />
              </td>
              <td v-if="hasLocationBasedInventory" class="text-center">
                {{ getAssignedLocationsDisplay(user) }}
              </td>
              <td class="text-center">
                <status-chip :status="user.status" />
              </td>
              <td class="text-center">
                <v-chip
                  v-if="!user.invited"
                  :color="user.mfaEnrolled ? 'success' : 'default'"
                  size="small"
                  label
                  data-test="user-2fa-chip"
                >
                  {{
                    user.mfaEnrolled
                      ? $t("security.status.enabled")
                      : $t("security.status.disabled")
                  }}
                </v-chip>
              </td>
              <td class="text-center">
                <v-menu>
                  <template v-slot:activator="{ props }">
                    <v-btn flat icon v-bind="props"><v-icon left>mdi-dots-vertical</v-icon></v-btn>
                  </template>
                  <v-list>
                    <v-list-item
                      v-for="(item, index) in getMenuItems(user)"
                      :key="index"
                      :value="index"
                      @click="item.action(user.id, user.email)"
                    >
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            <tr>
              <td :colspan="hasLocationBasedInventory ? 7 : 6" class="text-center">
                <v-pagination
                  v-model="page"
                  :length="Math.ceil(users.length / perPage)"
                ></v-pagination>
              </td>
            </tr>
          </tbody>
        </v-table>
      </v-main>
    </v-layout>
  </v-card>
</template>

<script lang="ts" setup>
import { useUserStore } from "@/users/stores/users";
import { UserListItem } from "@/users/types/users";
import { ref, computed, onMounted } from "vue";
import { useAuthStore } from "@/auth/stores/auth";
import { useAccountStore } from "@/account/stores/account";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import UserDialog from "@/users/components/user-management/UserDialog.vue";
import i18n from "@/app/plugins/i18n";
import BaseConfirmDialog from "@/shared/components/Common/BaseConfirmDialog.vue";
import ResetPasswordDialog from "@/auth/components/reset-password/ResetPasswordDialog.vue";
import StatusChip from "@/shared/components/Common/StatusChip.vue";
enum ORDER {
  up = "up",
  down = "down",
}

interface UserWithStatus extends UserListItem {
  status: string;
}

type SortableUserKey = "name" | "role" | "status";

const users = ref<UserWithStatus[]>([]);
const deleteDialogVisible = ref(false);
const deleteDialogLoading = ref(false);
const deactivateDialogVisible = ref(false);
const deactivateDialogLoading = ref(false);
const activateDialogVisible = ref(false);
const activateDialogLoading = ref(false);
const userToDelete = ref({});
const page = ref(1);
const dialogAction = ref("");
const perPage = ref(8);
const userDialog = ref<null | { open: (id?: string) => null }>(null);
const resetPassDialog = ref<null | {
  open: (id?: string, email?: string) => null;
}>(null);
const isOwner = computed(() => useAuthStore().isOwner);
const hasLocationBasedInventory = computed(() => {
  const account = useAccountStore().account;
  return account?.accountSettings?.inventory?.location_based_inventory_enabled === true;
});
const getStatus = () => {
  const allUsers = useUserStore().users;
  //TODO check account status after activate/deactivate is completed
  users.value = allUsers.map((user) => {
    return { ...user, status: user.invited ? "Invited" : "Active" };
  });
};

onMounted(() => {
  users.value = [];
  useUserStore()
    .fetchUsers()
    .then(() => {
      getStatus();
      orderBy("name");
    });
});

const headers = computed(() => {
  const baseHeaders: Array<{ title: string; path?: SortableUserKey; align?: string }> = [
    { title: "" },
    {
      title: i18n.global.t("page.users.title.name"),
      path: "name",
      align: "left",
    },
    {
      title: i18n.global.t("page.users.title.accessLevel"),
      path: "role",
      align: "center",
    },
    {
      title: i18n.global.t("common.action.status"),
      path: "status",
      align: "center",
    },
    {
      title: i18n.global.t("page.users.title.twoFactor"),
      align: "center",
    },
    { title: "" },
  ];

  if (hasLocationBasedInventory.value) {
    baseHeaders.splice(3, 0, {
      title: i18n.global.t("page.users.title.assignedLocations"),
      align: "center",
    });
  }

  return baseHeaders;
});

const paginatedUsers = computed(() => {
  const start = (page.value - 1) * perPage.value;
  const end = start + perPage.value;
  return users.value.slice(start, end);
});

const getAssignedLocationsDisplay = (user: UserWithStatus): string => {
  const assignedLocationIds = user?.assignedLocationIds;
  if (assignedLocationIds == null) {
    return "All";
  }
  if (Array.isArray(assignedLocationIds)) {
    return String(assignedLocationIds.length);
  }
  return "All";
};

// const sort = ref({ name: ORDER.up, role: ORDER.up });
const sort = ref<Partial<Record<SortableUserKey, ORDER>>>({});

// Methods
const showInvite = () => {
  dialogAction.value = "create";
  userDialog.value?.open();
};
const showEdit = (id: string) => {
  dialogAction.value = "edit";
  userDialog.value?.open(id);
};
const showDelete = (id?: string, email?: string) => {
  userToDelete.value = { uid: id, email };
  deleteDialogVisible.value = true;
};
const showReset = (id: string, email?: string) => {
  resetPassDialog.value?.open(id, email);
};

const showReinvite = async (email?: string) => {
  if (!email) return;
  await useUserStore().reinviteUser(email);
};

const deleteUser = async () => {
  deleteDialogLoading.value = true;

  await useUserStore().deleteUser(userToDelete.value);
  deleteDialogLoading.value = false;
  deleteDialogVisible.value = false;

  await useUserStore()
    .fetchUsers()
    .then(() => {
      getStatus();
      orderBy("name");
    });
};
const ownerList = [
  {
    title: i18n.global.t("common.action.resetPassword"),
    action: (id: string, email?: string) => showReset(id, email),
  },
  {
    title: i18n.global.t("common.action.editUser"),
    action: (id: string) => showEdit(id),
  },
  {
    title: i18n.global.t("common.action.deleteUser"),
    action: (id: string, email: string) => showDelete(id, email),
  },
];

const invitedList = [
  {
    title: i18n.global.t("common.action.reinviteUser"),
    action: (_id?: string, email?: string) => showReinvite(email),
  },
  {
    title: i18n.global.t("common.action.deleteUser"),
    action: (id?: string, email?: string) => showDelete(id, email),
  },
];

const staffList = [
  {
    title: i18n.global.t("common.action.resetPassword"),
    action: (id: string, email?: string) => showReset(id, email),
  },
];

const getMenuItems = (user: UserWithStatus) => {
  if (user?.invited || user?.status === "Invited") return invitedList;
  return isOwner.value ? ownerList : staffList;
};

//TODO bring back when activate/deactivate is completed

// const showActivateUser = (id: string) => {
//   uid.value = id;
//   activateDialogVisible.value = true;
// };
// const showDeactivateUser = (id: string) => {
//   uid.value = id;
//   deactivateDialogVisible.value = true;
// };

// const actionList = (user: UserWithStatus) => {
//   return user.status === i18n.global.t("page.accountSetup.inactive")
//     ? [
//         {
//           title: i18n.global.t("common.action.activate"),
//           action: (id: string) => showActivateUser(id),
//         },
//         ...sharedList,
//       ]
//     : [
//         {
//           title: i18n.global.t("common.action.deactivate"),
//           action: (id: string) => showDeactivateUser(id),
//         },
//         ...sharedList,
//       ];
// };
//TODO bring back when activate/deactivate is completed
// const deactivateUser = async () => {
//   deactivateDialogLoading.value = true;

//   await useUserStore().deactivateUser(uid.value);
//   deactivateDialogLoading.value = false;
//   deactivateDialogVisible.value = false;

//   await useUserStore()
//     .fetchUsers()
//     .then(() => {
//       getStatus();
//       orderBy("name");
//     });
// };

// const activateUser = async () => {
//   activateDialogLoading.value = true;
//   await useUserStore().activateUser(uid.value);
//   activateDialogVisible.value = false;
//   activateDialogLoading.value = false;
//   await useUserStore()
//     .fetchUsers()
//     .then(() => {
//       getStatus();
//       orderBy("name");
//     });
// };

const contActiveUsers = () => {
  return users.value?.filter((user) => user.status === "Active").length;
};

const contInvitedUsers = () => {
  return users.value?.filter((user) => user.status === "Invited").length;
};

const getArrow = (path: SortableUserKey): string => {
  return sort.value[path] ? sort.value[path] : "";
};

const orderBy = (path?: SortableUserKey) => {
  if (path) {
    if (!sort.value[path] || sort.value[path] === ORDER.up) {
      users.value = users.value.sort((a, b) => a[path].localeCompare(b[path]));
      sort.value = {};
      sort.value[path] = ORDER.down;
    } else {
      users.value = users.value.sort((a, b) => b[path].localeCompare(a[path]));
      sort.value = {};
      sort.value[path] = ORDER.up;
    }
  }
};

const handleUsersUpdated = async () => {
  await useUserStore()
    .fetchUsers()
    .then(() => {
      getStatus();
      orderBy("name");
    });
};
</script>
